import { useState } from 'react'
import { redirect, useLoaderData, useNavigate, Link } from 'react-router'
import { BranchPageHeading } from '@/components/subscription/branch-page-heading'
import { Panel } from '@/components/subscription/panel'
import { formatDate } from '@/lib/shipment'
import { createClient } from '@/api-client'
import { Subscriptions } from '@/api-client/Subscriptions'
import { Checkbox } from '@/components/subscription/checkbox'
import { Subscription, SubscriptionSchedule } from '@/api-client/data-contracts'

export const loader = async () => {
  const subscriptions = await fetchSubscriptions()
  const subscription = subscriptions.data.data[0]

  if (!subscription) {
    return redirect('/subscription/rose/mypage')
  }

  // お届け日の変更は月1回契約のみ
  if (subscription.relationships.inventories.data.length !== 1) {
    return redirect('/subscription/rose/mypage')
  }

  const schedules = await fetchPlannedSchedules(subscription, deliverWeekday(subscription.attributes.next_deliver_at))
  const defaultSchedule = findDefaultSchedule(schedules.data.data, subscription.attributes.next_deliver_at)
  const preview = await fetchPreview(subscription, defaultSchedule?.relationships.inventory.data.id)

  return [subscriptions, schedules, preview] as const
}

function fetchSubscriptions() {
  return createClient(Subscriptions).getSubscriptions({ category: 'rose' })
}

function fetchPlannedSchedules(subscription: Subscription, weekday: number) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdSchedulesPlan(subscription.id, {
    per_month: '4',
    weekday: weekday.toString(),
  })
}

function fetchPreview(subscription: Subscription, inventoryId?: string) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdSchedulesPreview(subscription.id, { inventory_id: inventoryId ?? '' })
}

function findDefaultSchedule(schedules: SubscriptionSchedule[], nextDeliverAt: number) {
  const sorted = [...schedules].sort((a, b) => a.attributes.deliver_at[0] - b.attributes.deliver_at[0])

  return sorted.find((schedule) => schedule.attributes.deliver_at[0] === nextDeliverAt) ?? sorted[0]
}

// 火・水曜日着は火曜発送（2）、それ以外は金曜発送（5）
function deliverWeekday(nextDeliverAt: number) {
  const day = new Date(nextDeliverAt * 1000).getDay()

  return day === 2 || day === 3 ? 2 : 5
}

export const SubscriptionRoseMypageNext: React.FC = () => {
  const [
    {
      data: { data: subscriptions },
    },
    {
      data: { data: schedules },
    },
    {
      data: { data: preview },
    },
  ] = useLoaderData<typeof loader>()
  const navigate = useNavigate()
  const subscription = subscriptions[0]

  const sortedSchedules = [...schedules].sort((a, b) => a.attributes.deliver_at[0] - b.attributes.deliver_at[0])
  const defaultSchedule =
    sortedSchedules.find((schedule) => schedule.attributes.deliver_at[0] === subscription.attributes.next_deliver_at) ?? sortedSchedules[0]
  const [selectedId, setSelectedId] = useState(defaultSchedule?.id)
  const [futureSchedules, setFutureSchedules] = useState(preview)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const weekdayLabel = defaultSchedule ? formatDate(defaultSchedule.attributes.deliver_at[0] * 1000, 'E曜日') : ''

  const handleSelect = async (schedule: SubscriptionSchedule) => {
    setSelectedId(schedule.id)

    const { data } = await fetchPreview(subscription, schedule.relationships.inventory.data.id)
    setFutureSchedules(data.data)
  }

  const handleSubmit: React.MouseEventHandler<HTMLButtonElement> = async () => {
    const selected = sortedSchedules.find((schedule) => schedule.id === selectedId)

    if (!selected) {
      return
    }

    setIsSubmitting(true)

    try {
      await createClient(Subscriptions).putSubscriptionsSubscriptionIdInventories(subscription.id, {
        'inventory_ids[]': [selected.relationships.inventory.data.id],
      })
      await navigate('/subscription/rose/mypage', { state: { message: '次のお届け日を変更しました' } })
    } catch (error) {
      console.error(error)
      setIsSubmitting(false)
    }
  }

  return (
    <div className="w-full space-y-8 pt-4 pb-18">
      <BranchPageHeading main="次のお届け日を変更" />

      <div className="space-y-4">
        <Panel>
          <p className="text-[15px] font-bold text-black">次に受け取りたい日を選んでください</p>
          <div className="flex flex-col items-start gap-1 w-full">
            {sortedSchedules.map((schedule) => (
              <Checkbox key={schedule.id} checked={selectedId === schedule.id} onChange={() => handleSelect(schedule)}>
                {formatDate(schedule.attributes.deliver_at[0] * 1000, 'M月d日（E）')}
              </Checkbox>
            ))}
          </div>
        </Panel>

        <Panel>
          <div className="flex flex-col gap-1">
            <h3 className="text-[15px] font-bold text-black">変更後のお届け予定</h3>
            <p className="text-[13px] text-[#828282] leading-normal tracking-[0.02em]">現在と同じ間隔（毎月・{weekdayLabel}）でお届けします</p>
          </div>
          <div className="flex flex-col w-full">
            {futureSchedules.map((schedule) => (
              <span key={schedule.id} className="w-full text-[14px] leading-none py-4 border-b border-[#eeeeee] last:border-b-0">
                {formatDate(schedule.attributes.deliver_at[0] * 1000, 'M月d日（E）')}
              </span>
            ))}
          </div>
        </Panel>
      </div>

      <div className="flex flex-col items-center gap-4">
        <button
          type="button"
          disabled={isSubmitting}
          onClick={handleSubmit}
          className="w-full py-5 bg-black text-white text-[15px] font-medium rounded-full cursor-pointer hover:bg-main disabled:bg-main transition-colors"
        >
          次のお届け日を変更
        </button>
      </div>
    </div>
  )
}
